import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../../shared/hooks/useApi";
import { AgencyBillingSetDefaultPaymentMethodParams } from "./types";

const STRIPE_PUBLISHABLE_KEY = "pk_live_dBuGzg1Hz7h4X1qvZ25CDIrM";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export default function ReplacePaymentMethod(props: { hasExisting: boolean }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Button colorScheme="blue" onClick={onOpen}>
        {props.hasExisting ? "Replace card" : "Add card"}
      </Button>
      {isOpen ? <ReplacePaymentMethodModal onClose={onClose} /> : null}
    </>
  );
}

function ReplacePaymentMethodModal(props: { onClose: () => void }) {
  const { queries } = useApi();
  const setupIntentQuery = useQuery(queries.agencyBilling.setupIntent());

  return (
    <Modal isOpen={true} size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update payment method</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {setupIntentQuery.isLoading ? (
            <Box py={10} textAlign="center">
              <Spinner />
            </Box>
          ) : setupIntentQuery.isError || setupIntentQuery.data === undefined ? (
            <Box>Failed to start payment method setup. Please try again.</Box>
          ) : (
            <Elements
              options={{ clientSecret: setupIntentQuery.data.clientSecret }}
              stripe={stripePromise}
            >
              <PaymentForm onClose={props.onClose} />
            </Elements>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function PaymentForm(props: { onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { api, queries } = useApi();

  const mutation = useMutation({
    mutationFn: async () => {
      if (stripe === null || elements === null) {
        throw new Error("Stripe is not ready");
      }

      const { error: submitError } = await elements.submit();
      if (submitError !== undefined) {
        throw new Error(submitError.message ?? "Failed to validate payment details");
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (error !== undefined) {
        throw new Error(error.message ?? "Failed to confirm payment method");
      }
      if (setupIntent === undefined || setupIntent.status !== "succeeded") {
        throw new Error("Setup did not complete");
      }
      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent.payment_method?.id ?? null;
      if (paymentMethodId === null) {
        throw new Error("Stripe did not return a payment method");
      }

      const body: AgencyBillingSetDefaultPaymentMethodParams = { paymentMethodId };
      return await api.post("./billing/payment_method/default", {
        body,
      });
    },
    onSuccess: () => {
      toast({ status: "success", title: "Payment method updated" });
      queryClient.invalidateQueries({ queryKey: queries.agencyBilling.summary().queryKey });
      props.onClose();
    },
    onError: (err: Error) => {
      toast({
        status: "error",
        title: "Failed to update payment method",
        description: err.message,
      });
    },
  });

  return (
    <Box>
      <AddressElement options={{ mode: "billing" }} />
      <Box mt={4}>
        <PaymentElement />
      </Box>
      <ModalFooter px={0}>
        <Button mr={3} variant="ghost" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          isDisabled={stripe === null || elements === null}
          isLoading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Save
        </Button>
      </ModalFooter>
    </Box>
  );
}
